import type { HandIconName } from "./icons/registry";

/*
教科書のカテゴリーの表。学習サイトの一覧 (/luagate/textbook)、HP の見本 (/women)、開発中の見本 (/dev/luagate-w1) が、ここだけを読む。
並びは一覧の絞り込みの札の順 (基礎から業界へ)。絵は札の左に出す手描きの絵。

難易度 (level) は仮の値 (10/19、分野を 8 つに分け直した 10/21 に引き直し)。代表が決めた基準ではなく、分野を、読む前に要る知識の量で並べただけ。
分野の中でも冊によって難しさは違う (例 インフラとクラウドの Linux は初級、コンテナとクラウドは中級寄り)。冊ごとに持つ形は報告に案を書いた。
決まったらこの表だけを直す。

カテゴリーは管理者が自由な字で付ける欄なので、表に無い名前の冊も公開されうる。その冊だけ、絵は「読みもの」の絵になり、難易度は出さない (ほかの冊はそのまま)。
新しいカテゴリーは、この表に 1 行足す
*/
export const TEXTBOOK_CATEGORIES = [
  { name: "IT の基礎", icon: "cat-it-basics", level: "入門" },
  { name: "ネットワークと Web", icon: "cat-network", level: "初級" },
  { name: "インフラとクラウド", icon: "cat-infra-cloud", level: "中級" },
  { name: "データとセキュリティ", icon: "cat-data", level: "初級" },
  { name: "プログラミング", icon: "cat-programming", level: "初級" },
  { name: "開発の進め方", icon: "cat-dev-process", level: "中級" },
  // 考え方と PC の使い方の入り口 (2026-10-21)
  { name: "仕事の基礎", icon: "cat-presentation", level: "入門" },
  // お客さんの業界と会社の仕事の入り口 (2026-10-21)。読む前に IT の知識が要らないので入門
  { name: "業界と業務", icon: "nav-jobs", level: "入門" },
] as const satisfies readonly { name: string; icon: HandIconName; level: "入門" | "初級" | "中級" }[];

export type TextbookCategory = (typeof TEXTBOOK_CATEGORIES)[number]["name"];

export const TEXTBOOK_CATEGORY_NAMES: readonly TextbookCategory[] = TEXTBOOK_CATEGORIES.map((c) => c.name);

function entryOf(category: string | null | undefined) {
  return category ? TEXTBOOK_CATEGORIES.find((c) => c.name === category) : undefined;
}

export function isTextbookCategory(category: string | null | undefined): category is TextbookCategory {
  return entryOf(category) !== undefined;
}

/** 表に無いカテゴリーの絵。表紙の無い冊の絵 (textbook-art の TEXTBOOK_ART_FALLBACK) と同じ「読みもの」の絵。HP の頭が読むので textbook-art の表は読み込まない */
export const TEXTBOOK_CATEGORY_FALLBACK_ICON: HandIconName = "nav-textbook";

/** カテゴリーの絵。表に無い名前は「読みもの」の絵 */
export function textbookCategoryIcon(category: string | null | undefined): HandIconName {
  return entryOf(category)?.icon ?? TEXTBOOK_CATEGORY_FALLBACK_ICON;
}

/** カテゴリーから決めた難易度。表に無い名前は決められないので出さない */
export function textbookLevelOf(category: string | null | undefined): string | undefined {
  return entryOf(category)?.level;
}

/** 並べた名前を表の順にし、表に無い名前は後ろへ名前の順に置く (重なりは 1 つにする) */
export function sortTextbookCategories(names: Iterable<string>): string[] {
  const rank = (c: string) => {
    const i = (TEXTBOOK_CATEGORY_NAMES as readonly string[]).indexOf(c);
    return i === -1 ? TEXTBOOK_CATEGORIES.length : i;
  };
  return [...new Set(names)].sort((x, y) => rank(x) - rank(y) || x.localeCompare(y));
}
